<template>
  <aside v-if="visible && content" class="plate-hover-card" :style="tooltipStyle" @mouseenter="$emit('mouseenter')"
    @mouseleave="$emit('mouseleave', $event)">
    <button type="button" class="tooltip-close-button" :aria-label="labels.close" :title="labels.close"
      @click.stop="$emit('close', $event)">
      <i class="fas fa-times"></i>
    </button>

    <header class="tooltip-header">
      <p class="tooltip-group">{{ content.groupLabel }}</p>
      <h4 class="tooltip-title">{{ content.plateName }}</h4>
      <div class="tooltip-code-wrapper" v-if="content.plateCode || content.previous">
        <p v-if="content.plateCode" class="tooltip-code">
          {{ content.plateCode }}
        </p>
        <span v-if="content.previous" class="info-badge neutral previous-badge">
          {{ content.previous }}
        </span>
      </div>
    </header>

    <section v-if="content.highlights && content.highlights.length" class="tooltip-section">
      <h5 class="tooltip-section-title">{{ labels.activeHighlights }}</h5>
      <ul class="badge-list">
        <li v-for="(highlight, index) in content.highlights" :key="`highlight-${index}`"
          :class="['info-badge', getHighlightBadgeClass(highlight)]">
          {{ getHighlightBadgeLabel(highlight) }}
        </li>
      </ul>
    </section>

    <section v-if="content.metrics && content.metrics.length" class="tooltip-section">
      <ul class="tooltip-grid metrics-grid">
        <li v-for="(metric, index) in content.metrics" :key="`metric-${index}`" class="tooltip-item metric-item">
          <span class="tooltip-label">{{ metric.label }}</span>
          <span class="tooltip-value">{{ metric.value }}</span>
        </li>
      </ul>
    </section>

    <section v-if="content.details && content.details.length" class="tooltip-section">
      <h5 class="tooltip-section-title">{{ labels.details }}</h5>
      <ul class="tooltip-grid details-grid">
        <li v-for="(detail, index) in content.details" :key="`detail-${index}`" class="tooltip-item">
          <span class="tooltip-label">{{ detail.label }}</span>
          <span class="tooltip-value" :style="getDetailValueStyle(detail)">{{ detail.value }}</span>
        </li>
      </ul>
    </section>

    <section v-if="content.frequency && content.frequency.length" class="tooltip-section">
      <h5 class="tooltip-section-title">{{ labels.frequency }}</h5>
      <article v-for="(frequency, index) in content.frequency" :key="`frequency-${index}`" class="frequency-card">
        <h6 class="frequency-title">{{ frequency.title }}</h6>
        <ul class="frequency-stats">
          <li v-for="(stat, statIndex) in frequency.stats" :key="`stat-${index}-${statIndex}`" class="frequency-chip">
            {{ stat }}
          </li>
        </ul>
      </article>
    </section>

    <section v-if="content.flexibleRules && content.flexibleRules.length" class="tooltip-section">
      <h5 class="tooltip-section-title">{{ labels.flexibleRules }}</h5>
      <ul class="badge-list">
        <li v-for="(rule, index) in content.flexibleRules" :key="`rule-${index}`" class="info-badge neutral">
          {{ rule }}
        </li>
      </ul>
    </section>
  </aside>
</template>

<script>
import { t } from 'i18next';

export default {
  name: 'PlateHoverCard',
  emits: ['mouseenter', 'mouseleave', 'close'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    position: {
      type: Object,
      default: () => ({ top: 0, left: 0 })
    },
    content: {
      type: Object,
      default: null
    }
  },
  computed: {
    labels() {
      return {
        close: t('Fechar'),
        details: t('plate_hover_details_title'),
        frequency: t('plate_hover_frequency_title'),
        flexibleRules: t('plate_hover_flexible_rules_title'),
        activeHighlights: t('plate_hover_active_highlights_title')
      };
    },
    tooltipStyle() {
      return {
        top: `${this.position?.top || 0}px`,
        left: `${this.position?.left || 0}px`,
        maxHeight: `${this.position?.maxHeight || 0}px`,
        width: this.position?.width ? `${this.position.width}px` : null,
        maxWidth: this.position?.maxWidth ? `${this.position.maxWidth}px` : null
      };
    }
  },
  methods: {
    getDetailValueStyle(detail) {
      if (!detail?.toneStyle) return null;
      return detail.toneStyle;
    },
    getHighlightBadgeLabel(highlight) {
      if (highlight && typeof highlight === 'object') {
        return highlight.label || '';
      }
      return String(highlight || '');
    },
    getHighlightBadgeClass(highlight) {
      const type = highlight && typeof highlight === 'object'
        ? String(highlight.highlightType || 'conflict').trim().toLowerCase()
        : 'conflict';

      if (type === 'usage') return 'usage';
      if (type === 'candidate') return 'candidate';
      return 'conflict';
    }
  }
};
</script>

<style scoped lang="less">
.plate-hover-card {
  position: fixed;
  z-index: 9999;
  width: min(340px, calc(100vw - 24px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  overscroll-behavior: contain;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid rgba(27, 41, 87, 0.18);
  background:
    linear-gradient(180deg, rgba(247, 249, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
  box-shadow:
    0 24px 48px rgba(16, 24, 40, 0.18),
    0 8px 16px rgba(16, 24, 40, 0.12);
  backdrop-filter: blur(10px);
  pointer-events: auto;
  color: #16213e;
  scrollbar-width: thin;
  scrollbar-color: rgba(53, 76, 132, 0.55) rgba(203, 212, 233, 0.6);
}

.tooltip-close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(223, 231, 250, 0.92);
  color: #415a92;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}

.tooltip-close-button:hover {
  background: rgba(198, 210, 240, 0.96);
  color: #1d2850;
}

.tooltip-close-button i {
  font-size: 0.7rem;
}

.plate-hover-card::-webkit-scrollbar {
  width: 8px;
}

.plate-hover-card::-webkit-scrollbar-track {
  background: rgba(203, 212, 233, 0.45);
  border-radius: 999px;
}

.plate-hover-card::-webkit-scrollbar-thumb {
  background: rgba(53, 76, 132, 0.55);
  border-radius: 999px;
}

.tooltip-header {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 8px;
  padding-right: 24px;
}

.tooltip-group {
  margin: 0;
  font-size: clamp(0.56rem, 0.54rem + 0.14vw, 0.63rem);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b6b96;
}

.tooltip-title {
  margin: 0;
  font-size: clamp(0.72rem, 0.68rem + 0.23vw, 0.81rem);
  font-weight: 800;
  line-height: 1.25;
  color: #1d2850;
}

.tooltip-code-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.previous-badge {
  font-size: 0.55rem;
  padding: 2px 6px;
}

.tooltip-code {
  margin: 0;
  font-size: clamp(0.59rem, 0.56rem + 0.14vw, 0.68rem);
  font-weight: 700;
  color: #34518f;
}

.tooltip-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(27, 41, 87, 0.1);
}

.tooltip-section-title {
  margin: 0 0 6px;
  font-size: clamp(0.56rem, 0.54rem + 0.14vw, 0.63rem);
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b6b96;
}

.tooltip-grid {
  display: grid;
  gap: 5px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.metrics-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.details-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.tooltip-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 6px 7px;
  border-radius: 8px;
  background: rgba(227, 234, 249, 0.45);
}

.metric-item {
  background: rgba(223, 231, 250, 0.72);
}

.tooltip-label {
  font-size: clamp(0.52rem, 0.5rem + 0.11vw, 0.59rem);
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #6676a3;
}

.tooltip-value {
  font-size: clamp(0.63rem, 0.6rem + 0.16vw, 0.7rem);
  font-weight: 700;
  line-height: 1.35;
  color: #172348;
  word-break: break-word;
}

.frequency-card {
  padding: 6px 7px;
  border-radius: 8px;
  background: rgba(242, 245, 252, 0.95);
}

.frequency-card+.frequency-card {
  margin-top: 5px;
}

.frequency-title {
  margin: 0;
  font-size: clamp(0.61rem, 0.58rem + 0.14vw, 0.68rem);
  font-weight: 800;
  color: #1d2850;
}

.frequency-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 5px;
  margin-bottom: 0;
  padding: 0;
  list-style: none;
}

.frequency-chip {
  padding: 2px 5px;
  border-radius: 999px;
  background: rgba(213, 223, 247, 0.95);
  font-size: clamp(0.56rem, 0.53rem + 0.11vw, 0.63rem);
  font-weight: 700;
  color: #28457e;
}

.badge-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.info-badge {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 3px 6px;
  border-radius: 999px;
  font-size: clamp(0.56rem, 0.53rem + 0.11vw, 0.63rem);
  font-weight: 700;
  line-height: 1.3;
}

.info-badge.neutral {
  background: rgba(222, 231, 246, 0.88);
  color: #28457e;
}

.info-badge.usage {
  background: rgba(222, 245, 228, 0.96);
  color: #1f6b3a;
}

.info-badge.candidate {
  background: rgba(255, 234, 214, 0.96);
  color: #8a4a00;
}

.info-badge.conflict {
  background: rgba(255, 223, 223, 0.96);
  color: #992222;
}

@media (max-width: 1440px),
(max-height: 920px) {
  .plate-hover-card {
    width: min(320px, calc(100vw - 20px));
    padding: 8px;
    border-radius: 10px;
  }

  .tooltip-header {
    gap: 2px;
    margin-bottom: 6px;
  }

  .tooltip-section {
    margin-top: 6px;
    padding-top: 6px;
  }

  .tooltip-section-title {
    margin-bottom: 4px;
  }

  .tooltip-grid,
  .frequency-stats,
  .badge-list {
    gap: 4px;
  }

  .tooltip-item,
  .frequency-card {
    padding: 5px 6px;
    border-radius: 7px;
  }

  .info-badge {
    min-height: 18px;
    padding: 2px 5px;
  }
}

@media (max-width: 768px) {
  .plate-hover-card {
    width: min(280px, calc(100vw - 16px));
    padding: 8px;
    border-radius: 10px;
  }

  .metrics-grid,
  .details-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-height: 760px) {
  .plate-hover-card {
    width: min(300px, calc(100vw - 18px));
    padding: 7px;
  }

  .tooltip-header {
    gap: 2px;
    margin-bottom: 5px;
  }

  .tooltip-section {
    margin-top: 5px;
    padding-top: 5px;
  }

  .tooltip-grid,
  .frequency-stats,
  .badge-list {
    gap: 3px;
  }

  .tooltip-item,
  .frequency-card {
    padding: 4px 5px;
  }

  .info-badge {
    min-height: 18px;
    padding: 2px 5px;
  }
}
</style>
